import type { AnnotationEntry } from '@geosoft/viewer-sdk';
import { usePluginStore } from '@/state/usePluginStore';
import { listAnnotationModes } from './annotationPlugin';

interface AnnotationPanelProps {
  readonly selectedPoint: readonly [number, number] | null;
  readonly annotations: ReadonlyArray<AnnotationEntry>;
  readonly onAddPoint: () => void;
}

export function AnnotationPanel({ selectedPoint, annotations, onAddPoint }: AnnotationPanelProps) {
  const { transient, updateAnnotationTransient } = usePluginStore();
  const currentMode = transient.annotation.mode;
  const modeIcons = {
    point: 'push_pin',
    line: 'polyline',
    polygon: 'crop_free',
    rectangle: 'crop_3_2',
    circle: 'circle',
    select: 'near_me'
  };

  return (
    <>
      <div className="annotation-terra-draw-info">
        <p>
          <span className="material-symbols-outlined">draw</span>
          Draw annotations with Terra Draw integration. Bridge Plan persistence active.
        </p>
      </div>

      <div className="annotation-mode-selector">
        <h4>Drawing Mode</h4>
        <div className="plugin-mode-grid" role="radiogroup" aria-label="Annotation drawing modes">
          {listAnnotationModes().map((mode) => (
            <button
              key={mode}
              type="button"
              className={`plugin-mode-button ${currentMode === mode ? 'active' : ''}`}
              onClick={() => updateAnnotationTransient({ mode })}
              role="radio"
              aria-checked={currentMode === mode}
              title={`${mode} annotation mode`}
            >
              <span className="material-symbols-outlined">
                {modeIcons[mode as keyof typeof modeIcons]}
              </span>
              {mode}
            </button>
          ))}
        </div>
      </div>

      <div className="annotation-current-point">
        <strong>Current point:</strong>{' '}
        {selectedPoint ? (
          <span className="coordinates">
            {selectedPoint[0].toFixed(4)}, {selectedPoint[1].toFixed(4)}
          </span>
        ) : (
          <span className="no-selection">none selected</span>
        )}
      </div>

      <div className="plugin-actions">
        <button 
          type="button" 
          onClick={onAddPoint} 
          disabled={!selectedPoint}
          className="primary"
        >
          <span className="material-symbols-outlined">add_location</span>
          Add {currentMode} annotation
        </button>
      </div>

      <div className="annotation-store-status">
        <div className="store-info">
          <span className="material-symbols-outlined">storage</span>
          <strong>{annotations.length}</strong> annotations in Bridge store
        </div>
        
        {annotations.length > 0 && (
          <div className="annotation-list">
            <h4>Recent Annotations</h4>
            <ol className="plugin-list" aria-label="Recent annotations">
              {annotations.slice(0, 5).map((annotation) => (
                <li key={annotation.id} className="annotation-item">
                  <div className="annotation-header">
                    <span className="material-symbols-outlined annotation-type-icon">
                      {modeIcons[annotation.kind as keyof typeof modeIcons] || 'place'}
                    </span>
                    <strong>{String(annotation.properties.label ?? annotation.kind)}</strong>
                    <span className="annotation-kind">({annotation.kind})</span>
                  </div>
                  <div className="annotation-meta">
                    <span className="created-by">{annotation.createdBy}</span>
                    <span className="created-at">
                      {new Date(annotation.createdAt).toLocaleTimeString()}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
            {annotations.length > 5 && (
              <p className="more-annotations">
                ... and {annotations.length - 5} more
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}
